import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Toolbar,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useUser } from '@/providers/UserContext';

import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useNavigate, useParams } from 'react-router';
import { getPlot, deletePlot, runPlot, finalizePlot } from '@/actions/plot';
import { getPlotWaypoints } from '@/actions/plotWaypoints';
import { getPlotMarkers } from '@/actions/plotMarkers';
import { getPlotAsset, getPlotAssetBinary } from '@/actions/plotAsset';
import PlotDeleteDialog from '../../PlotDeleteDialog';
import PlotMarkers from './PlotMarkers';
import PlotContents from './PlotContents';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import PLOT_STATUSES from '../../../plotStatuses';
import type { Plot as PlotType, PlotWaypoint, EntityId } from '@/types';
// import { LoadingButton } from '@mui/lab';
// import {
//   getPlotAsset,
//   getPlotAssetBinary,
// } from '../../../../actions/plotAsset';
// import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

const POLL_INTERVAL = 5000; // 5 sec

function Plot(): React.JSX.Element {
  const user = useUser();
  const { plot_id } = useParams<{ plot_id: string }>();
  const navigate = useNavigate();

  const api = useApi();
  const [plot, setPlot] = useState<PlotType | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [finalizing, setFinalizing] = useState<boolean>(false);
  const [isReadyForGeneration, setIsReadyForGeneration] =
    useState<boolean>(false);
  const [waypointsData, setWaypointsData] = useState<PlotWaypoint[]>([]);
  const [waypointsLoading, setWaypointsLoading] = useState<boolean>(true);
  const [contentsRefreshTrigger, setContentsRefreshTrigger] =
    useState<number>(0);
  const [content_manifest, setContentManifest] = useState<{
    document?: string;
    [key: string]: unknown;
  } | null>(null);
  const loadingRef = useRef<boolean>(false);

  const loadWaypoints = useCallback(async () => {
    if (!plot_id || loadingRef.current || !api) return;

    try {
      loadingRef.current = true;
      setWaypointsLoading(true);
      const data = await getPlotWaypoints(api, plot_id as EntityId);
      const waypoints = data.results || [];
      setWaypointsData(waypoints);
    } catch (error) {
      console.error('Error loading waypoints:', error);
      setWaypointsData([]);
    } finally {
      loadingRef.current = false;
      setWaypointsLoading(false);
    }
  }, [api, plot_id]);

  const checkPlotReadiness = useCallback(
    async (waypoints: PlotWaypoint[]) => {
      if (!plot_id || !waypoints || waypoints.length === 0) {
        setIsReadyForGeneration(false);
        return;
      }

      try {
        // Check each waypoint for markers
        let allWaypointsHaveMarkers = true;
        let allMarkersReady = true;

        if (!api) return;
        for (const waypoint of waypoints) {
          const markersData = await getPlotMarkers(
            api,
            plot_id as EntityId,
            waypoint.entity_id,
          );
          const markers = markersData.results || [];

          // Check if waypoint has any markers
          if (markers.length === 0) {
            allWaypointsHaveMarkers = false;
            break;
          }

          // Check if all markers are in 'ready' status
          const notReadyMarkers = markers.filter(
            (marker) => marker.status !== 'ready',
          );
          if (notReadyMarkers.length > 0) {
            allMarkersReady = false;
            break;
          }
        }

        setIsReadyForGeneration(allWaypointsHaveMarkers && allMarkersReady);
      } catch (error) {
        console.error('Error checking plot readiness:', error);
        setIsReadyForGeneration(false);
      }
    },
    [api, plot_id],
  );

  const fetchPlot = useCallback(() => {
    if (user?.entity_id && plot_id && api) {
      getPlot(api, plot_id as EntityId)
        .then((data) => {
          setPlot(data);
        })
        .catch((error) => {
          console.log(error);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load plot',
              errorDetails:
                error instanceof Error
                  ? error.message
                  : 'Unable to retrieve the requested plot. This could be due to network issues, server problems, or the plot may no longer exist.',
              returnPath: '/cartographer',
            },
          });
        });
    }
  }, [user?.entity_id, plot_id, api, navigate]);

  useEffect(() => {
    if (user?.entity_id && !plot) {
      fetchPlot();
    }
  }, [user?.entity_id, plot_id, plot, fetchPlot]);

  // Reset waypoints data when plot changes
  useEffect(() => {
    setWaypointsData([]);
    setWaypointsLoading(true);
    loadingRef.current = false;
  }, [plot_id]);

  // Load waypoints when plot loads (for all statuses)
  useEffect(() => {
    if (plot && waypointsData.length === 0) {
      loadWaypoints();
    }
  }, [plot, loadWaypoints, waypointsData.length]);

  // Check plot readiness when waypoints data changes (only for draft plots)
  useEffect(() => {
    if (plot?.status === PLOT_STATUSES.DRAFT && waypointsData.length > 0) {
      checkPlotReadiness(waypointsData);
    } else if (plot?.status !== PLOT_STATUSES.DRAFT) {
      // For non-draft plots, set readiness to false since we don't need the generate button
      setIsReadyForGeneration(false);
    }
  }, [plot?.status, waypointsData, checkPlotReadiness]);

  // Periodic readiness check for draft plots (every 10 seconds)
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (plot?.status === PLOT_STATUSES.DRAFT && waypointsData.length > 0) {
      interval = setInterval(() => {
        checkPlotReadiness(waypointsData);
      }, 10000); // Check every 10 seconds
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [plot?.status, waypointsData, checkPlotReadiness]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    console.log('running effect');
    if (
      plot?.status &&
      [
        PLOT_STATUSES.PREPARING,
        PLOT_STATUSES.PENDING,
        PLOT_STATUSES.RUNNING,
        PLOT_STATUSES.FINALIZING,
        'queued', // Add queued status for polling after generation
      ].includes(plot.status)
    ) {
      console.log('setting interval');
      interval = setInterval(fetchPlot, POLL_INTERVAL);
    }
    return () => {
      if (interval) {
        console.log('clearing interval');
        clearInterval(interval);
      }
    };
  }, [plot?.status, fetchPlot]);

  useEffect(() => {
    console.log(plot);
    if (
      plot?.status === PLOT_STATUSES.COMPLETE &&
      plot?.content_id &&
      content_manifest === null &&
      api
    ) {
      getPlotAsset(api, plot.entity_id, 'manifest.json')
        .then((manifest) => {
          console.log(manifest);
          setContentManifest(
            manifest as unknown as {
              document?: string;
              [key: string]: unknown;
            },
          );
        })
        .catch((error) => {
          console.log(error);
          setContentManifest({});
        });
    }
  }, [plot, content_manifest, api]);

  if (!plot || waypointsLoading || !plot_id) {
    return <LoadingMask />;
  }

  const base_path = '/cartographer/plot';

  const handleDeletePlot = async () => {
    if (!api || !plot_id) return;
    try {
      await deletePlot(api, plot_id as EntityId);
      navigate(base_path);
    } catch (error) {
      console.error('Error deleting plot:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to delete the plot. This could be due to network issues, server problems, or insufficient permissions.';
      setApiError(errorMessage);
    }
  };

  const handleGenerateContents = async () => {
    if (!api || !plot_id) return;
    try {
      setGenerating(true);
      setApiError(null);
      await runPlot(api, plot_id as EntityId);
      // Refresh the plot to get updated status
      fetchPlot();
      // Trigger refresh of plot contents to get updated element statuses
      setContentsRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error generating contents:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to generate plot contents. This could be due to network issues, server problems, or insufficient permissions.';
      setApiError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleFinalizePlot = async () => {
    if (!api || !plot_id) return;
    try {
      setFinalizing(true);
      setApiError(null);
      await finalizePlot(api, plot_id as EntityId);
      // Refresh the plot to get updated status
      fetchPlot();
    } catch (error) {
      console.error('Error finalizing plot:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to finalize plot. This could be due to network issues, server problems, or insufficient permissions.';
      setApiError(errorMessage);
    } finally {
      setFinalizing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!api) return;
    try {
      if (!plot?.entity_id || !content_manifest?.document) return;
      // Make API call to fetch the file
      const response = await getPlotAssetBinary(
        api,
        plot.entity_id,
        content_manifest.document,
      );
      console.log(response);
      // Extract filename from Content-Disposition header, if provided
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${plot.name}.docx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to download the plot report. This could be due to network issues, server problems, or the report may not be available.';
      setApiError(errorMessage);
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton sx={{ mr: 2 }} onClick={() => navigate(base_path)}>
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {plot.name}
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() =>
              navigate({
                pathname: `${base_path}/edit`,
                search: `?plot_id=${plot_id}`,
              })
            }
            startIcon={<EditOutlinedIcon />}
            disabled={plot?.status === PLOT_STATUSES.COMPLETE}
          >
            Edit
          </Button>
          {plot?.status === PLOT_STATUSES.COMPLETE ? (
            <Button
              onClick={handleDownloadReport}
              startIcon={<DownloadOutlinedIcon />}
              disabled={!content_manifest?.document}
            >
              Download
            </Button>
          ) : undefined}

          <Button
            onClick={() => setDeleteOpen(true)}
            startIcon={<DeleteOutlinedIcon />}
            disabled={plot?.status === PLOT_STATUSES.COMPLETE}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        <Typography variant='subtitle1'>Basic Information</Typography>
        <Divider />
        <br />
        <Grid
          container
          component='dl' // mount a Definition List
          spacing={0}
          sx={{ display: 'table' }}
        >
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {plot?.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {plot?.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(plot?.created_at, true)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Last Updated:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(plot?.updated_at, true)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box padding='15px'>
        <PlotMarkers
          plotId={plot_id}
          waypoints={waypointsData}
          plotStatus={plot?.status}
          onMarkersChange={async () => {
            // Refresh waypoints data first, then check readiness
            try {
              setWaypointsLoading(true);
              if (!api) return;
              const data = await getPlotWaypoints(api, plot_id as EntityId);
              const freshWaypoints = data.results || [];
              setWaypointsData(freshWaypoints);
              // Check readiness with fresh data
              checkPlotReadiness(freshWaypoints);
            } catch (error) {
              console.error('Error refreshing waypoints:', error);
            } finally {
              setWaypointsLoading(false);
            }
          }}
        />
      </Box>

      {plot?.status === PLOT_STATUSES.DRAFT && (
        <Box padding='15px'>
          <Button
            onClick={handleGenerateContents}
            disabled={!isReadyForGeneration || generating}
            variant='contained'
            color='primary'
            size='large'
          >
            {generating ? 'Generating...' : 'Generate Contents'}
          </Button>
        </Box>
      )}

      {plot?.status === PLOT_STATUSES.PENDING && (
        <Box padding='15px'>
          <Button
            onClick={handleFinalizePlot}
            disabled={finalizing}
            variant='contained'
            color='primary'
            size='large'
          >
            {finalizing ? 'Finalizing...' : 'Finalize Plot'}
          </Button>
        </Box>
      )}

      <Box padding='15px'>
        <PlotContents
          plotId={plot_id}
          refreshTrigger={contentsRefreshTrigger}
          plotStatus={plot?.status}
        />
      </Box>

      <PlotDeleteDialog
        key={`delete_${plot?.updated_at}`}
        plot={plot}
        open={deleteOpen}
        deletePlot={handleDeletePlot}
        handleClose={() => setDeleteOpen(false)}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default Plot;
